/**
 * ProfileAboutSection — personal info visible on OTHER user's profiles.
 * Shows: profession, interests, age, gender, country (respecting privacy settings).
 * Own profile uses hamburger for this; this section is for viewing others.
 * Future: block, report, share actions will be added here.
 */

import React, { useMemo } from 'react'
import { Briefcase, MapPin, User as UserIcon, Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCategories } from '@turnifly/shared/hooks'
import { differenceInYears } from 'date-fns'
import type { Profile, Category } from '@turnifly/shared/types'

interface ProfileAboutSectionProps {
  profile: Profile
  isOwnProfile: boolean
}

export const ProfileAboutSection: React.FC<ProfileAboutSectionProps> = ({
  profile, isOwnProfile,
}) => {
  const { t, i18n } = useTranslation()
  const { data: categories } = useCategories()

  // Resolve interest IDs to category objects
  const resolvedInterests = useMemo((): Category[] => {
    if (!profile.interests.length || !categories?.length) return []
    const catMap = new Map(
      ((categories ?? []) as Category[]).map((c) => [c.id, c]),
    )
    return profile.interests
      .map((id) => catMap.get(id))
      .filter((c): c is Category => c !== undefined)
  }, [profile.interests, categories])

  // Calculate age from birth_date
  const age = useMemo((): number | null => {
    if (!profile.birth_date) return null
    return differenceInYears(new Date(), new Date(profile.birth_date))
  }, [profile.birth_date])

  // Determine visible info items
  const infoItems: Array<{ icon: React.ReactNode; text: string }> = []

  if (profile.profession) {
    infoItems.push({ icon: <Briefcase size={14} />, text: profile.profession })
  }

  if (profile.show_gender && profile.gender) {
    const genderLabel = profile.gender === 'male'
      ? t('profile.about.male')
      : t('profile.about.female')
    infoItems.push({ icon: <UserIcon size={14} />, text: genderLabel })
  }

  if (profile.show_age && age !== null) {
    infoItems.push({
      icon: <Calendar size={14} />,
      text: `${age} ${t('profile.about.years')}`,
    })
  }

  if (profile.show_region && profile.country) {
    infoItems.push({ icon: <MapPin size={14} />, text: profile.country })
  }

  // Nothing to show
  const hasInfo = infoItems.length > 0
  const hasInterests = resolvedInterests.length > 0

  if (!hasInfo && !hasInterests) return null

  // On own profile, hamburger handles this -- show only on other's profile
  if (isOwnProfile) return null

  return (
    <div className="px-4 mt-4">
      <div className="rounded-2xl bg-white/[0.02] border border-white/[0.05] p-4 space-y-3">
        {/* Info items */}
        {hasInfo && (
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {infoItems.map((item, i) => (
              <div key={i} className="flex items-center gap-1.5 text-sm text-text/70">
                <span className="text-text-hint/60">{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Interests chips */}
        {hasInterests && (
          <div className="flex flex-wrap gap-1.5">
            {resolvedInterests.map((cat) => {
              const label = i18n.language === 'uz' ? cat.name_uz : cat.name_en
              return (
                <span
                  key={cat.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                    text-[11px] bg-white/[0.04] border border-white/[0.06] text-text-hint"
                >
                  <span>{cat.icon}</span>
                  <span>{label}</span>
                </span>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
